const CONTROLLER_KEY = '__sfilatoArticleMeasurementV1';
const ROOT_SELECTOR = '[data-editorial-measurement]';
const SCROLL_THRESHOLDS = [25, 50, 75, 90];

function safeIdentifier(value, fallback = '') {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);

  return normalized || fallback;
}

function safeNumericIdentifier(value) {
  const normalized = String(value || '').trim();
  return /^\d{1,30}$/.test(normalized) ? normalized : '';
}

function safeDate(value) {
  const normalized = String(value || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : '';
}

function nowMilliseconds() {
  return typeof window.performance?.now === 'function' ? window.performance.now() : Date.now();
}

function requestFrame(callback) {
  if (typeof window.requestAnimationFrame === 'function') {
    return window.requestAnimationFrame(callback);
  }
  return window.setTimeout(callback, 16);
}

function analyticsProcessingAllowed() {
  const privacyApi = window.Shopify?.customerPrivacy;
  if (!privacyApi || typeof privacyApi.analyticsProcessingAllowed !== 'function') return false;

  try {
    return privacyApi.analyticsProcessingAllowed() === true;
  } catch (_error) {
    return false;
  }
}

function publishEvent(eventName, payload) {
  if (!analyticsProcessingAllowed()) return false;

  try {
    const result = window.Shopify?.analytics?.publish?.(eventName, payload);
    result?.catch?.(() => {});
  } catch (_error) {
    // Measurement must never interrupt reading or navigation.
  }

  if (window.dataLayer && typeof window.dataLayer.push === 'function') {
    try {
      window.dataLayer.push({ event: eventName, ...payload });
    } catch (_error) {
      // A third-party dataLayer implementation must not break the article.
    }
  }

  try {
    window.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
  } catch (_error) {
    // CustomEvent is an optional integration surface in older browsers.
  }

  return true;
}

function destinationFromUrl(anchor) {
  try {
    return new URL(anchor.href, window.location.origin);
  } catch (_error) {
    return null;
  }
}

function destinationHandle(url, segment) {
  const parts = url.pathname.split('/').filter(Boolean);
  const index = parts.indexOf(segment);
  return index >= 0 ? safeIdentifier(parts[index + 1]) : '';
}

function createController() {
  const states = new Map();
  const sentMilestones = new Set();
  let scrollFrame = 0;

  function metadataFromRoot(root) {
    return {
      article_id: safeNumericIdentifier(root.dataset.articleId || root.dataset.editorialArticleId),
      article_handle: safeIdentifier(root.dataset.articleHandle),
      locale: safeIdentifier(root.dataset.articleLocale, 'en'),
      section: safeIdentifier(root.dataset.articleSection, 'sfilato-magazine'),
      author_id: safeIdentifier(root.dataset.articleAuthorId, 'sfilato-editorial-team'),
      published_date: safeDate(root.dataset.articlePublishedDate),
      measurement_version: 1,
    };
  }

  function pageScope(metadata) {
    return `${window.location.pathname}:${metadata.article_id || metadata.article_handle}`;
  }

  function emitOnce(state, eventName, suffix, extra = {}) {
    const key = `${state.scope}:${eventName}:${suffix}`;
    if (sentMilestones.has(key)) return false;

    const emitted = publishEvent(eventName, { ...state.metadata, ...extra });
    if (emitted) sentMilestones.add(key);
    return emitted;
  }

  function updateActiveClock(state, now = nowMilliseconds()) {
    const pageIsActive =
      analyticsProcessingAllowed() &&
      document.visibilityState === 'visible' &&
      (typeof document.hasFocus !== 'function' || document.hasFocus()) &&
      state.inViewport;

    if (!pageIsActive) {
      state.lastActiveAt = null;
      return;
    }

    if (state.lastActiveAt !== null) {
      state.activeMilliseconds += Math.min(Math.max(now - state.lastActiveAt, 0), 2000);
    }
    state.lastActiveAt = now;
  }

  function articleProgress(state) {
    const content = state.content;
    if (!content?.isConnected) return 0;

    const rect = content.getBoundingClientRect();
    if (rect.height <= 0) return 0;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const traversed = Math.min(Math.max(viewportHeight - rect.top, 0), rect.height);
    return (traversed / rect.height) * 100;
  }

  function maybeEmitView(state) {
    if (!state.inViewport) return;
    emitOnce(state, 'magazine_view', 'visible');
  }

  function checkScrollState(state) {
    if (!state.root.isConnected) return;

    updateActiveClock(state);
    maybeEmitView(state);
    const progress = articleProgress(state);

    for (const threshold of SCROLL_THRESHOLDS) {
      if (progress < threshold) continue;
      emitOnce(state, 'article_scroll', threshold, {
        depth_percent: threshold,
        active_seconds: Math.max(0, Math.floor(state.activeMilliseconds / 1000)),
      });
    }
  }

  function checkAllScrollStates() {
    scrollFrame = 0;
    for (const state of states.values()) checkScrollState(state);
  }

  function scheduleScrollCheck() {
    if (scrollFrame) return;
    scrollFrame = requestFrame(checkAllScrollStates);
  }

  function observeState(state) {
    state.observer?.disconnect();

    if (typeof window.IntersectionObserver !== 'function') {
      state.inViewport = true;
      maybeEmitView(state);
      scheduleScrollCheck();
      return;
    }

    state.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.find((item) => item.target === state.root);
        if (!entry) return;
        updateActiveClock(state);
        state.inViewport = entry.isIntersecting;
        updateActiveClock(state);
        maybeEmitView(state);
        scheduleScrollCheck();
      },
      { threshold: 0.01 },
    );
    state.observer.observe(state.root);
  }

  function registerRoot(root) {
    const metadata = metadataFromRoot(root);
    if (!metadata.article_id && !metadata.article_handle) return;

    const scope = pageScope(metadata);
    let state = states.get(scope);

    if (!state) {
      state = {
        scope,
        metadata,
        root,
        content: root.querySelector('[data-editorial-article-content]') || root,
        observer: null,
        inViewport: false,
        activeMilliseconds: 0,
        lastActiveAt: null,
      };
      states.set(scope, state);
    } else if (state.root !== root) {
      updateActiveClock(state);
      state.root = root;
      state.content = root.querySelector('[data-editorial-article-content]') || root;
      state.metadata = metadata;
      state.inViewport = false;
      state.lastActiveAt = null;
    } else {
      return;
    }

    root.dataset.editorialMeasurementReady = 'true';
    observeState(state);
  }

  function scan(container = document) {
    if (container.matches?.(ROOT_SELECTOR)) registerRoot(container);
    container.querySelectorAll?.(ROOT_SELECTOR).forEach(registerRoot);
    scheduleScrollCheck();
  }

  function stateForAnchor(anchor) {
    const root = anchor.closest(ROOT_SELECTOR);
    if (!root) return null;
    const metadata = metadataFromRoot(root);
    return states.get(pageScope(metadata)) || null;
  }

  function linkClassification(anchor, destination) {
    const explicit = safeIdentifier(anchor.dataset.magazineLink);
    if (explicit) return explicit;

    if (
      destination.origin === window.location.origin &&
      destination.pathname.replace(/\/$/, '') === '/pages/editorial-standards'
    ) {
      return 'correction';
    }

    if (anchor.closest('.sfilato-related-stories') && destination.pathname.includes('/blogs/news/')) {
      return 'related';
    }

    if (
      destination.origin === window.location.origin &&
      (/^\/collections\//.test(destination.pathname) || /^\/products\//.test(destination.pathname))
    ) {
      return 'commerce';
    }

    return '';
  }

  function moduleForAnchor(anchor) {
    const explicit = safeIdentifier(anchor.dataset.module);
    if (explicit) return explicit;
    if (anchor.closest('.sfilato-article-wardrobe')) return 'article-wardrobe';
    if (anchor.closest('[data-editorial-article-content]')) return 'article-body';
    if (anchor.closest('.sfilato-related-stories')) return 'related-stories';
    if (anchor.closest('.sfilato-editorial-note')) return 'editorial-note';
    return 'article';
  }

  function trackLinkActivation(event) {
    if (event.type === 'auxclick' && event.button !== 1) return;
    if (event.type === 'click' && event.button !== 0) return;

    const anchor = event.target.closest?.('a[href]');
    if (!anchor) return;
    const state = stateForAnchor(anchor);
    if (!state) return;
    const destination = destinationFromUrl(anchor);
    if (!destination) return;

    const classification = linkClassification(anchor, destination);
    const position = Number.parseInt(anchor.dataset.position || '', 10);
    const base = {
      ...state.metadata,
      module: moduleForAnchor(anchor),
      ...(Number.isInteger(position) && position > 0 ? { position } : {}),
    };

    if (classification === 'related') {
      publishEvent('article_related_click', {
        ...base,
        target_article_id: safeNumericIdentifier(anchor.dataset.targetId),
        target_article_handle:
          safeIdentifier(anchor.dataset.targetHandle) || destinationHandle(destination, 'news'),
      });
      return;
    }

    if (classification === 'commerce') {
      const destinationType = /^\/products\//.test(destination.pathname) ? 'product' : 'collection';
      publishEvent('article_commerce_click', {
        ...base,
        destination_type: destinationType,
        destination_id:
          safeIdentifier(anchor.dataset.destinationId) || destinationHandle(destination, `${destinationType}s`),
      });
      return;
    }

    if (classification === 'correction' || classification === 'standards') {
      publishEvent('article_correction_open', {
        ...base,
        destination_id: 'editorial-standards',
      });
    }
  }

  function refreshAfterConsentChange() {
    const now = nowMilliseconds();
    for (const state of states.values()) {
      state.lastActiveAt = null;
      updateActiveClock(state, now);
      maybeEmitView(state);
      checkScrollState(state);
    }
  }

  function start() {
    scan(document);
    window.addEventListener('scroll', scheduleScrollCheck, { passive: true });
    window.addEventListener('resize', scheduleScrollCheck, { passive: true });
    window.addEventListener('focus', refreshAfterConsentChange);
    window.addEventListener('blur', refreshAfterConsentChange);
    document.addEventListener('visibilitychange', refreshAfterConsentChange);
    document.addEventListener('visitorConsentCollected', refreshAfterConsentChange);
    document.addEventListener('trackingConsentAccepted', refreshAfterConsentChange);
    document.addEventListener('click', trackLinkActivation);
    document.addEventListener('auxclick', trackLinkActivation);
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
    window.setInterval(() => {
      const now = nowMilliseconds();
      for (const [scope, state] of states) {
        if (!state.root.isConnected) {
          state.observer?.disconnect();
          states.delete(scope);
          continue;
        }
        updateActiveClock(state, now);
      }
    }, 1000);
  }

  return { scan, start };
}

const existingController = window[CONTROLLER_KEY];
if (existingController?.scan) {
  existingController.scan(document);
} else {
  const controller = createController();
  Object.defineProperty(window, CONTROLLER_KEY, {
    value: controller,
    configurable: false,
    enumerable: false,
    writable: false,
  });
  controller.start();
}
